import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import i18n from "@/i18n";

type PlanStatus = "pending" | "scheduled" | "completed" | "canceled";
type PlanSourceType = "manual" | "derived_from_application";

type ApplicationItemRelation = {
  id?: string;
  name?: string | null;
  description?: string | null;
  sale_price?: number | null;
  product_subtype?: { name?: string | null } | null;
} | null;

export type PetApplicationPlan = {
  id: string;
  clinic_id: string;
  pet_id: string;
  item_id: string;
  due_date: string;
  notes: string | null;
  status: PlanStatus;
  appointment_id: string | null;
  source_type: PlanSourceType;
  source_pet_application_id: string | null;
  completed_pet_application_id: string | null;
  completed_at: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  deleted_at: string | null;
  item?: ApplicationItemRelation;
  pet?: { id?: string; name?: string | null } | null;
  appointment?: { id?: string; starts_at?: string | null; status?: string | null; type?: string | null } | null;
};

export type PetApplicationPlanRow = {
  plan_id: string;
  clinic_id: string;
  pet_id: string;
  pet_name: string | null;
  owner_id: string | null;
  owner_name: string | null;
  item_id: string | null;
  item_name: string | null;
  due_date: string;
  status: PlanStatus;
  appointment_id: string | null;
  completed_pet_application_id: string | null;
  completed_at: string | null;
  source_type: PlanSourceType;
  source_pet_application_id: string | null;
  notes: string | null;
  is_overdue: boolean;
};

type CreatePetApplicationPlanInput = {
  clinic_id: string;
  pet_id: string;
  item_id: string;
  due_date: string;
  notes?: string | null;
  appointment_id?: string | null;
  source_type?: PlanSourceType;
  source_pet_application_id?: string | null;
  created_by?: string | null;
};

type UpdatePetApplicationPlanInput = Partial<CreatePetApplicationPlanInput> & {
  id: string;
  status?: PlanStatus;
  completed_pet_application_id?: string | null;
  completed_at?: string | null;
  canceled_at?: string | null;
};

type MatchPetApplicationPlanRow = {
  plan_id: string;
  clinic_id: string;
  pet_id: string;
  item_id: string;
  due_date: string;
  status: PlanStatus;
  appointment_id: string | null;
  source_type: PlanSourceType;
  notes: string | null;
  created_at: string;
  distance_seconds: number;
};

type QueryBuilderLike = PromiseLike<{ data: unknown; error: { message?: string } | null }> & {
  select: (query: string) => QueryBuilderLike;
  insert: (values: unknown) => QueryBuilderLike;
  update: (values: unknown) => QueryBuilderLike;
  eq: (column: string, value: unknown) => QueryBuilderLike;
  in: (column: string, values: unknown[]) => QueryBuilderLike;
  is: (column: string, value: unknown) => QueryBuilderLike;
  lte: (column: string, value: unknown) => QueryBuilderLike;
  order: (column: string, options?: { ascending?: boolean }) => Promise<{ data: unknown; error: { message?: string } | null }>;
  single: () => Promise<{ data: unknown; error: { message?: string } | null }>;
};

type RpcClient = {
  from: (table: string) => QueryBuilderLike;
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message?: string } | null }>;
};

const supabaseAny = supabase as unknown as RpcClient;

const invalidatePlanQueries = (queryClient: ReturnType<typeof useQueryClient>, petId?: string | null, clinicId?: string | null) => {
  if (petId) {
    queryClient.invalidateQueries({ queryKey: ["pet-application-plans", petId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ["pet-application-plans"] });
  }

  if (clinicId) {
    queryClient.invalidateQueries({ queryKey: ["application-plans-due", clinicId] });
    queryClient.invalidateQueries({ queryKey: ["application-plan-report-rows", clinicId] });
    queryClient.invalidateQueries({ queryKey: ["matchable-application-plans", clinicId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ["application-plans-due"] });
    queryClient.invalidateQueries({ queryKey: ["application-plan-report-rows"] });
    queryClient.invalidateQueries({ queryKey: ["matchable-application-plans"] });
  }
};

export function usePetApplicationPlans(
  petId?: string,
  options?: { statuses?: PlanStatus[] },
) {
  const statuses = options?.statuses ?? undefined;

  return useQuery({
    queryKey: ["pet-application-plans", petId, statuses?.join(",") ?? "all"],
    enabled: Boolean(petId),
    queryFn: async (): Promise<PetApplicationPlan[]> => {
      let query = supabaseAny
        .from("pet_application_plans")
        .select(`
          *,
          item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          pet:pets!pet_id(id, name),
          appointment:appointments(id, starts_at, status, type)
        `)
        .eq("pet_id", petId!)
        .is("deleted_at", null);

      if (statuses?.length) {
        query = query.in("status", statuses);
      }

      const { data, error } = await query.order("due_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PetApplicationPlan[];
    },
  });
}

export function useCreatePetApplicationPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePetApplicationPlanInput & { next_applications?: unknown }): Promise<PetApplicationPlan> => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { next_applications: _nextApplications, ...dbInput } = input;
      const payload = {
        ...dbInput,
        status: input.appointment_id ? "scheduled" : "pending",
        source_type: input.source_type ?? "manual",
      };

      const { data, error } = await supabaseAny
        .from("pet_application_plans")
        .insert(payload)
        .select(`
          *,
          item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          pet:pets!pet_id(id, name),
          appointment:appointments(id, starts_at, status, type)
        `)
        .single();

      if (error) throw error;
      return data as PetApplicationPlan;
    },
    onSuccess: (data) => {
      invalidatePlanQueries(queryClient, data.pet_id, data.clinic_id);
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("reports:vaccines.planForm.createSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("reports:vaccines.planForm.createError"));
    },
  });
}

export function useUpdatePetApplicationPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdatePetApplicationPlanInput & { next_applications?: unknown }): Promise<PetApplicationPlan> => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { next_applications: _nextApplications, ...dbInput } = input as typeof input & { next_applications?: unknown };
      const payload = {
        ...dbInput,
        status:
          input.status ??
          (Object.prototype.hasOwnProperty.call(input, "appointment_id")
            ? (input.appointment_id ? "scheduled" : "pending")
            : undefined),
      };

      const { data, error } = await supabaseAny
        .from("pet_application_plans")
        .update(payload)
        .eq("id", id)
        .is("deleted_at", null)
        .select(`
          *,
          item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          pet:pets!pet_id(id, name),
          appointment:appointments(id, starts_at, status, type)
        `)
        .single();

      if (error) throw error;
      const updatedPlan = data as PetApplicationPlan;

      // Keep derived plans in sync with the source application so history/upcoming
      // use the same next dose reference after reminder date edits.
      if (
        updatedPlan.source_type === "derived_from_application" &&
        updatedPlan.source_pet_application_id
      ) {
        const { error: syncError } = await supabaseAny
          .from("pet_applications")
          .update({
            has_next_dose: true,
            next_dose_date: updatedPlan.due_date,
            next_application_item_id: updatedPlan.item_id,
            next_dose_completed_at: null,
            next_dose_completed_by_application_id: null,
            appointment_id: updatedPlan.appointment_id,
          })
          .eq("id", updatedPlan.source_pet_application_id)
          .is("deleted_at", null);

        if (syncError) throw syncError;
      }

      return updatedPlan;
    },
    onSuccess: (data) => {
      invalidatePlanQueries(queryClient, data.pet_id, data.clinic_id);
      queryClient.invalidateQueries({ queryKey: ["pet-applications", data.pet_id] });
      if (data.source_pet_application_id) {
        queryClient.invalidateQueries({ queryKey: ["pet-application", data.source_pet_application_id] });
      }
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["applications-with-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      queryClient.invalidateQueries({ queryKey: ["owner-history-timeline"] });
      queryClient.invalidateQueries({ queryKey: ["owner-history-feed"] });
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("reports:vaccines.planForm.updateSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("reports:vaccines.planForm.updateError"));
    },
  });
}

export function useDeletePetApplicationPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }): Promise<{ petId: string; clinicId: string }> => {
      const deletedAt = new Date().toISOString();

      const { data: existingPlan, error: existingPlanError } = await supabaseAny
        .from("pet_application_plans")
        .select("pet_id, clinic_id")
        .eq("id", id)
        .is("deleted_at", null)
        .single();

      if (existingPlanError) throw existingPlanError;

      const { error: deleteError } = await supabaseAny.rpc("soft_delete_pet_application_plan", {
        p_plan_id: id,
        p_deleted_at: deletedAt,
      });

      if (deleteError) throw deleteError;

      const planMeta = existingPlan as { pet_id: string; clinic_id: string };
      return { petId: planMeta.pet_id, clinicId: planMeta.clinic_id };
    },
    onSuccess: ({ petId, clinicId }) => {
      invalidatePlanQueries(queryClient, petId, clinicId);
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("reports:vaccines.planForm.deleteSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("reports:vaccines.planForm.deleteError"));
    },
  });
}

export function useCancelPetApplicationPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }): Promise<PetApplicationPlan> => {
      const { data, error } = await supabaseAny
        .from("pet_application_plans")
        .update({
          status: "canceled",
          canceled_at: new Date().toISOString(),
          appointment_id: null,
        })
        .eq("id", id)
        .select(`
          *,
          item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          pet:pets!pet_id(id, name),
          appointment:appointments(id, starts_at, status, type)
        `)
        .single();

      if (error) throw error;
      return data as PetApplicationPlan;
    },
    onSuccess: (data) => {
      invalidatePlanQueries(queryClient, data.pet_id, data.clinic_id);
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("reports:vaccines.planForm.cancelSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("reports:vaccines.planForm.cancelError"));
    },
  });
}

export function useCompletePetApplicationPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      planId,
      completedPetApplicationId,
    }: {
      planId: string;
      completedPetApplicationId: string;
    }) => {
      const { data, error } = await supabaseAny.rpc("complete_pet_application_plan", {
        p_plan_id: planId,
        p_completed_pet_application_id: completedPetApplicationId,
        p_completed_at: new Date().toISOString(),
      });

      if (error) throw error;
      return data as string | null;
    },
    onSuccess: () => {
      invalidatePlanQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["future-rows-appointments"] });
      invalidateReportsCache(queryClient);
    },
  });
}

export function useApplicationPlansDue(clinicId?: string, days = 30) {
  return useQuery({
    queryKey: ["application-plans-due", clinicId, days],
    queryFn: async (): Promise<PetApplicationPlan[]> => {
      const today = new Date();
      const end = new Date(today);
      end.setDate(today.getDate() + days);

      let query = supabaseAny
        .from("pet_application_plans")
        .select(`
          *,
          item:items!item_id(id, name, description, product_subtype:product_subtypes(name)),
          pet:pets!pet_id(id, name)
        `)
        .in("status", ["pending", "scheduled"])
        .is("deleted_at", null)
        .lte("due_date", end.toISOString());

      if (clinicId) {
        query = query.eq("clinic_id", clinicId);
      }

      const { data, error } = await query.order("due_date", { ascending: true });

      if (error) throw error;
      return (data ?? []) as PetApplicationPlan[];
    },
  });
}

export function useApplicationPlanReportRows(
  clinicId?: string,
  input?: {
    petId?: string;
    statuses?: PlanStatus[];
    startDate?: string | null;
    endDate?: string | null;
  },
) {
  return useQuery({
    queryKey: [
      "application-plan-report-rows",
      clinicId,
      input?.petId ?? null,
      input?.statuses?.join(",") ?? "all",
      input?.startDate ?? null,
      input?.endDate ?? null,
    ],
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PetApplicationPlanRow[]> => {
      const startDate = input?.startDate ? `${input.startDate.slice(0, 10)}T00:00:00` : null;
      const endDate = input?.endDate ? `${input.endDate.slice(0, 10)}T23:59:59.999` : null;

      const { data, error } = await supabaseAny.rpc("get_pet_application_plan_rows", {
        p_clinic_id: clinicId,
        p_pet_id: input?.petId ?? null,
        p_statuses: input?.statuses ?? null,
        p_start_date: startDate,
        p_end_date: endDate,
      });

      if (error) throw error;
      return (data ?? []) as PetApplicationPlanRow[];
    },
  });
}

export function useMatchableApplicationPlans(input: {
  clinicId?: string;
  petId?: string;
  itemId?: string | null;
  applicationDate?: string | Date | null;
  windowDays?: number;
  enabled?: boolean;
}) {
  const applicationDateValue =
    input.applicationDate instanceof Date
      ? input.applicationDate.toISOString()
      : input.applicationDate ?? null;

  return useQuery({
    queryKey: [
      "matchable-application-plans",
      input.clinicId ?? null,
      input.petId ?? null,
      input.itemId ?? null,
      applicationDateValue,
      input.windowDays ?? 30,
    ],
    enabled:
      input.enabled !== false &&
      Boolean(input.clinicId && input.petId && input.itemId && applicationDateValue),
    queryFn: async (): Promise<MatchPetApplicationPlanRow[]> => {
      const { data, error } = await supabaseAny.rpc("match_pet_application_plan", {
        p_pet_id: input.petId,
        p_item_id: input.itemId,
        p_application_date: applicationDateValue,
        p_clinic_id: input.clinicId,
        p_window_days: input.windowDays ?? 30,
      });

      if (error) throw error;
      return (data ?? []) as MatchPetApplicationPlanRow[];
    },
  });
}
